import { useState, useEffect } from "react";
import LoginForm from "@/components/LoginForm";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import TrialDialog from "@/components/TrialDialog";
import ModulesShowcase from "@/components/layout/ModulesShowcase";


// Import the logo image directly for better caching
const logoImage = "/lovable-uploads/e155b6e3-9cbb-41ce-b033-0655ff11da65.png";
const ingantLogo = "/lovable-uploads/25a60cad-7663-40f4-8897-39f385f54f39.png";

// Module descriptions for the carousel with updated image for Administration to a man with beard in B&W
const moduleDescriptions = {
  "administration": {
    title: "Administration",
    description: "Manage users and permissions with insights using powerful admin controls",
    image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "talent-acquisition": {
    title: "HR",
    description: "Streamline talent acquisition requirements, define organization structure and roles, find candidates using AI automation",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "sales": {
    title: "Sales",
    description: "Boost sales performance through building an effective Account Business Plan, with deep AI-assisted customer insights, mapping stakeholders, build business value framework, and orchestrate effective engagements",
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "marketing": {
    title: "Marketing",
    description: "Drive effective marketing GTM through AI-assisted orchestration in budgeting, content creation, sentiment analysis and the 4P's (Product, Price, Place and Promotion)",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "finance": {
    title: "Finance",
    description: "AI-empowered finance insights into sales performance, forecast and revenue trends, intelligent gap and margin analysis, and building bridge plans",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "legal": {
    title: "Legal",
    description: "Examine local laws, past cases and precedents, and compare country laws using advanced AI assistance",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "performance": {
    title: "Performance",
    description: "Manage employee performance through goal setting, appraisals, performance banding, and improvement planning with AI-powered insights",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  },
  "projects": {
    title: "Projects",
    description: "Expedite project planning, utilize Gantt charts for scheduling, and manage resources with powerful AI guidance",
    image: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=800&h=800&fit=crop&auto=format&q=80&sat=-100"
  }
};

const MainFrame = ({ autoOpenProjectsDemo = false }: { autoOpenProjectsDemo?: boolean }) => {
  const [selectedModule, setSelectedModule] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [api, setApi] = useState<any>(null);
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const [showTrialDialog, setShowTrialDialog] = useState(false);

  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      const nextSlide = (currentSlide + 1) % Object.keys(moduleDescriptions).length;
      api.scrollTo(nextSlide);
      setCurrentSlide(nextSlide);
    }, 5000);
    return () => clearInterval(interval);
  }, [api, currentSlide]);

  const handleModuleChange = (value: string) => {
    setSelectedModule(value);
    const moduleKeys = Object.keys(moduleDescriptions);
    const newIndex = moduleKeys.findIndex(key => key === value);
    if (newIndex !== -1 && api) {
      api.scrollTo(newIndex);
      setCurrentSlide(newIndex);
    }
  };

  return (
    <div className="container relative z-10 px-6 py-6 mx-auto flex-grow">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row mb-6 items-start justify-between">
          <div className="md:w-1/2">
            <div className="flex flex-row items-center">
              {/* Left Logo Image */}
              <div className="mr-3">
                <img src={logoImage} alt="InganT Image" className="h-20 w-auto" />
              </div>
              {/* Adjusted layout for Ingant logo and tagline */}
              <div className="flex flex-col items-start justify-center h-20">
                {/* Updated Ingant logo with adjusted positioning */}
                <div>
                  <img src={ingantLogo} alt="Ingant Logo" className="h-[43px] w-auto mb-1" />
                </div>
                {/* Tagline with position adjusted */}
                <h2 className="text-[11px] text-primary animate-fade-in md:text-[11px] font-light" style={{
                  animationDelay: "0.3s"
                }}>
                  AI Solutions For Human Good
                </h2>
              </div>
            </div>
          </div>
          
          {/* Positioned at the same level as HumanGood.AI but aligned with login section */}
          <div className="md:w-1/2 max-w-[520px]">
            <p className="text-[26px] text-indigo-100/80 animate-fade-in font-light text-center" style={{
              animationDelay: "0.5s"
            }}>
              Empowering business functions with the power of AI to maximize human potential
            </p>
          </div>
        </div>
      
        <div className="flex flex-col lg:flex-row items-start justify-between gap-[15px] max-w-6xl mx-auto">
          {/* Left side: Carousel with improved image display */}
          <div className="w-full lg:w-1/2">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden h-full shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]">
              <Carousel setApi={setApi} className="w-full h-full">
                <CarouselContent>
                  {Object.entries(moduleDescriptions).map(([key, { image, title, description }], index) => (
                    <CarouselItem key={key} className="h-full">
                      <div className="relative h-full">
                        <img src={image} alt={title} className="w-full h-[500px] object-cover object-center brightness-105 hover:brightness-110 transition-all duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-8 text-left">
                          <h3 className="text-3xl font-bold mb-3 text-white">{title}</h3>
                          <p className="text-lg text-gray-200">{description}</p>
                        </div>
                        
                        {/* Improved indicator dots */}
                        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-3">
                          {Object.keys(moduleDescriptions).map((_, i) => (
                            <button
                              key={i}
                              onClick={() => {
                                if (api) {
                                  api.scrollTo(i);
                                  setCurrentSlide(i);
                                }
                              }}
                              className={`w-3 h-3 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-primary scale-125 shadow-[0_0_10px_rgba(155,135,245,0.9)]' : 'bg-white/50 hover:bg-white/80'}`}
                              aria-label={`Go to slide ${i + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                
                {/* Improved navigation buttons */}
                <div className="absolute bottom-1/2 transform translate-y-1/2 left-4 right-4 flex justify-between">
                  <button
                    onClick={() => {
                      if (api) {
                        api.scrollPrev();
                        const prevSlide = (currentSlide - 1 + Object.keys(moduleDescriptions).length) % Object.keys(moduleDescriptions).length;
                        setCurrentSlide(prevSlide);
                      }
                    }}
                    className="bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-lg transition-all border border-white/30"
                    aria-label="Previous slide"
                  >
                    <ArrowLeft className="h-6 w-6 text-white" />
                  </button>
                  <button
                    onClick={() => {
                      if (api) {
                        api.scrollNext();
                        const nextSlide = (currentSlide + 1) % Object.keys(moduleDescriptions).length;
                        setCurrentSlide(nextSlide);
                      }
                    }}
                    className="bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-lg transition-all border border-white/30"
                    aria-label="Next slide"
                  >
                    <ArrowRight className="h-6 w-6 text-white" />
                  </button>
                </div>
              </Carousel>
            </div>
          </div>
          
          {/* Right side: Login form */}
          <div className="w-full lg:w-1/2 max-w-[520px] mx-auto lg:mx-0 flex flex-col">
            <div className="bg-white/10 backdrop-blur-xl p-10 rounded-2xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.3)] border border-white/20 w-full animate-fade-in" style={{
              animationDelay: "0.4s"
            }}>
              <h3 className="text-2xl font-semibold mb-6 text-white text-left">Login</h3>
          
              {/* Added light separator line */}
              <Separator className="bg-gray-400/30 mb-6" />
              
              <div className="mb-6">
                <Label htmlFor="module-select" className="text-indigo-100 mb-3 block">
                  Select module:
                </Label>
                <RadioGroup value={selectedModule} onValueChange={handleModuleChange} className="space-y-1 w-full max-w-[310px]">
                  {/* Responsive grid: 2 cols on mobile/portrait, 4 cols on larger screens */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2 gap-y-1">
                    <div className={`flex items-center p-2 rounded-md transition-colors ${selectedModule === 'administration' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="administration" id="administration" className="border-white/50" />
                      <Label htmlFor="administration" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Admin</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md transition-colors ${selectedModule === 'talent-acquisition' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="talent-acquisition" id="talent-acquisition" className="border-white/50" />
                      <Label htmlFor="talent-acquisition" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">HR</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md transition-colors ${selectedModule === 'sales' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="sales" id="sales" className="border-white/50" />
                      <Label htmlFor="sales" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Sales</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md w-[95px] transition-colors ${selectedModule === 'marketing' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="marketing" id="marketing" className="border-white/50" />
                      <Label htmlFor="marketing" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Marketing</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md w-[80px] transition-colors ${selectedModule === 'finance' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="finance" id="finance" className="border-white/50" />
                      <Label htmlFor="finance" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Finance</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md w-[80px] transition-colors ${selectedModule === 'projects' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="projects" id="projects" className="border-white/50" />
                      <Label htmlFor="projects" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Projects</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md transition-colors ${selectedModule === 'legal' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="legal" id="legal" className="border-white/50" />
                      <Label htmlFor="legal" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Legal</Label>
                    </div>
                    <div className={`flex items-center p-2 rounded-md w-[110px] transition-colors ${selectedModule === 'performance' ? 'bg-primary/30 data-[state=checked]:border-secondary' : 'hover:bg-white/10'}`}>
                      <RadioGroupItem value="performance" id="performance" className="border-white/50" />
                      <Label htmlFor="performance" className="text-indigo-100 cursor-pointer whitespace-nowrap text-[12px] ml-1">Performance</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
              
              <LoginForm selectedModule={selectedModule} />
            </div>
        
            {/* Action buttons repositioned to be between login section and carousel bottom */}
            <div className="flex flex-col sm:flex-row gap-6 mt-8 justify-center items-center">
              <Button size="lg" className="bg-secondary text-white hover:bg-white hover:text-black w-[180px] border border-white/20" onClick={() => setShowTrialDialog(true)}>
                Try it out
              </Button>
              <Button className="bg-secondary text-white hover:bg-white w-[180px] hover:text-black border border-white/20" size="lg" onClick={() => setShowDemoVideo(true)}>
                Watch demo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Modules showcase sections ===== */}
      <ModulesShowcase autoOpenProjectsDemo={autoOpenProjectsDemo} />



      {/* Demo Video Dialog */}
      <Dialog open={showDemoVideo} onOpenChange={setShowDemoVideo}>
        <DialogContent className="sm:max-w-[80%] p-0 border-none bg-transparent">
          <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1920/1080' }}>
            <iframe 
              src="https://share.synthesia.io/embeds/videos/16b4d5bf-63aa-4606-b195-7d13383b1377" 
              loading="lazy" 
              title="Synthesia video player - Ingant demo video" 
              allowFullScreen 
              allow="encrypted-media; fullscreen;" 
              style={{ 
                position: 'absolute', 
                width: '100%', 
                height: '100%', 
                top: 0, 
                left: 0, 
                border: 'none', 
                padding: 0, 
                margin: 0, 
                overflow: 'hidden' 
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Trial Dialog */}
      <TrialDialog open={showTrialDialog} onOpenChange={setShowTrialDialog} />
    </div>
  );
};

export default MainFrame;
